import React, { FC } from 'react';

import { EAssetType, ESectionOfferType } from 'constants/enums';
import { CirclePlus } from 'lucide-react';

import DataTable from 'components/DataTableComponent/DataTable';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Button } from 'components/ui/button';
import UploadImage from 'components/UploadImage/UploadImage';

import { SectionsItem } from '../../StoreTheme/storeTheme.types';

import { FormField } from './FormField';
import { useSectionsColumns } from './useSectionsColumns';
import { SECTION_TYPE_OPTIONS, useSectionsDraft } from './useSectionsDraft';

export type { StoreSectionsFormValues } from './sectionsSectionFields.types';

export const SectionsSectionFields: FC = () => {
  const {
    draft,
    setDraft,
    editedSectionId,
    idError,
    sectionTypeSelected,
    addOrSaveSection,
    removeRow,
    startEdit,
    tableRows,
    addDisabled
  } = useSectionsDraft();

  const sectionTableColumns = useSectionsColumns({ startEdit, removeRow });

  return (
    <div className="min-w-0 [&_input:not([aria-invalid])]:border-zinc-200 [&_input]:placeholder:text-base-mutedForeground">
      <FormField
        label="Section Name"
        name="storeSettingsSectionNameDraft"
        value={draft.name}
        onChange={(e) =>
          setDraft((d) => ({ ...d, name: e.target.value }))
        }
        placeholder="Name"
        dataTestId="sectionName"
      />
      <div className="mt-6 grid grid-cols-1 items-start gap-9 md:grid-cols-2">
        <FormField
          label="Section ID"
          name="storeSettingsSectionIdDraft"
          value={draft.publisherSectionId}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              publisherSectionId: e.target.value
            }))
          }
          placeholder="ID"
          error={Boolean(idError)}
          helperText={idError || undefined}
          dataTestId="sectionId"
        />
        <div className="min-w-0 [&_label]:!mb-2 [&_label]:text-[14px] [&_label]:font-medium [&_label]:leading-4 [&_label]:text-[#26282B]">
          <InputSelectDropdown
            label="Section Type"
            placeholder="Type"
            options={SECTION_TYPE_OPTIONS}
            defaultSelected={sectionTypeSelected}
            onOptionChange={(value) =>
              setDraft((d) => ({
                ...d,
                offerType: value as ESectionOfferType
              }))
            }
            disabled={Boolean(editedSectionId)}
            buttonDataTestId="storeSettingsSectionOfferType"
            listDataTestId="storeSettingsSectionOfferTypeList"
            buttonClassName="!border !border-solid !border-[var(--base-input,#E4E4E7)] shadow-none"
            contentClassName="!border !border-solid !border-[var(--base-input,#E4E4E7)]"
          />
        </div>
      </div>
      <UploadImage
        title="Icon"
        buttonText="Upload"
        onUpload={(url) =>
          setDraft((d) => ({ ...d, image: url }))
        }
        selectedImageUrl={draft.image}
        forceType={EAssetType.SECTION}
        variant="storeSettings"
        className="mt-9 w-full"
        data-testid="sectionImage"
        buttonDataTestId="uploadStoreSettingsSectionIcon"
      />
      <Button
        type="button"
        variant="outline"
        className="mt-9 h-10 w-full self-stretch items-center justify-center gap-2 rounded-md border-0 bg-base-secondary px-4 py-2 font-sans text-[14px] font-medium text-base-secondaryForeground shadow-none hover:bg-base-secondary"
        disabled={addDisabled}
        onClick={addOrSaveSection}
        data-testid="storeSettingsSectionAdd"
      >
        {editedSectionId ? (
          'Save Section'
        ) : (
          <>
            <CirclePlus className="h-5 w-5 text-base-secondaryForeground" />
            Add Section
          </>
        )}
      </Button>
      {tableRows.length > 0 && (
        <DataTable<SectionsItem>
          data={tableRows}
          columns={sectionTableColumns}
          rowIdIdentifier="publisherSectionId"
          showPagination={false}
          rowsPerPage={500}
          stickyHeader
          className="mt-9 min-w-0"
        />
      )}
    </div>
  );
};
